import {
  IconAdjustmentsHorizontal,
  IconArrowBarToDown,
  IconArrowDown,
  IconArrowsShuffle,
  IconArrowUp,
  IconAward,
  IconBeach,
  IconBell,
  IconBellOff,
  IconBuildingBank,
  IconCalendar,
  IconCalendarOff,
  IconCalendarTime,
  IconCalendarWeek,
  IconCashRegister,
  IconChartBar,
  IconCheck,
  IconChevronDown,
  IconChevronLeft,
  IconChevronRight,
  IconCircleCheck,
  IconCircleDashed,
  IconClock,
  IconCoins,
  IconCreditCard,
  IconDevices,
  IconDotsVertical,
  IconFileAlert,
  IconFilter,
  IconFirstAidKit,
  IconGift,
  IconGripVertical,
  IconHeart,
  IconHelpCircle,
  IconHome,
  IconHourglass,
  IconLayoutColumns,
  IconLayoutGrid,
  IconLayoutSidebar,
  IconLink,
  IconList,
  IconLogout,
  IconMail,
  IconMailOpened,
  IconMailX,
  IconMapPin,
  IconMessage2,
  IconMessageCircle,
  IconMinus,
  IconPackage,
  IconPencil,
  IconPin,
  IconPlane,
  IconPlaylistAdd,
  IconPlus,
  IconReceipt,
  IconReportAnalytics,
  IconRocket,
  IconSchool,
  IconSearch,
  IconSettings,
  IconShoe,
  IconSparkles,
  IconSpeakerphone,
  IconStack2,
  IconTag,
  IconToolsKitchen2,
  IconTrendingUp,
  IconUser,
  IconUserCheck,
  IconUserMinus,
  IconUserPlus,
  IconUsers,
  IconUserX,
  IconWalk,
  IconWallet,
  IconWaveSine,
  IconX,
} from "@tabler/icons-react";
import type { Icon } from "@tabler/icons-react";

/**
 * The calendar redesign handoff names its icons in Lucide. The app ships only
 * Tabler (no lucide-react; supply-chain install freeze), so every design icon
 * resolves through this map. Use `DESIGN_ICONS.Utensils` etc. instead of
 * picking a Tabler glyph per call site. Add a row here before using a new
 * design icon.
 */
export const DESIGN_ICONS = {
  // Time block types
  Utensils: IconToolsKitchen2,
  Users: IconUsers,
  GraduationCap: IconSchool,
  Plane: IconPlane,
  User: IconUser,
  CalendarOff: IconCalendarOff,
  // Need to know flags
  Hourglass: IconHourglass,
  Sparkles: IconSparkles,
  UserCheck: IconUserCheck,
  CircleDashed: IconCircleDashed,
  CircleCheck: IconCircleCheck,
  FileWarning: IconFileAlert,
  Pin: IconPin,
  BellOff: IconBellOff,
  MailX: IconMailX,
  Award: IconAward,
  CalendarClock: IconCalendarTime,
  UserX: IconUserX,
  Megaphone: IconSpeakerphone,
  Heart: IconHeart,
  Tag: IconTag,
  // Header / toolbar
  Link: IconLink,
  Calendar: IconCalendar,
  CalendarDays: IconCalendarWeek,
  List: IconList,
  LayoutGrid: IconLayoutGrid,
  Filter: IconFilter,
  ChevronDown: IconChevronDown,
  ChevronLeft: IconChevronLeft,
  ChevronRight: IconChevronRight,
  Check: IconCheck,
  // Week Display dropdown
  LayoutPanelLeft: IconLayoutSidebar,
  Columns3: IconLayoutColumns,
  // Zoom
  Minus: IconMinus,
  Plus: IconPlus,
  // Walk-in waitlist (ticket 4)
  Footprints: IconShoe,
  PersonStanding: IconWalk,
  GripVertical: IconGripVertical,
  Shuffle: IconArrowsShuffle,
  MessageSquareQuote: IconMessage2,
  Clock: IconClock,
  Pencil: IconPencil,
  UserMinus: IconUserMinus,
  UserPlus: IconUserPlus,
  ListPlus: IconPlaylistAdd,
  ListEnd: IconArrowBarToDown,
  Layers: IconStack2,
  ArrowUp: IconArrowUp,
  ArrowDown: IconArrowDown,
  Search: IconSearch,
  X: IconX,
  // Navigation: rail sections
  House: IconHome,
  TrendingUp: IconTrendingUp,
  ChartColumn: IconChartBar,
  CreditCard: IconCreditCard,
  Wallet: IconWallet,
  // Navigation: section links
  Receipt: IconReceipt,
  CashRegister: IconCashRegister,
  UsersRound: IconUsers,
  Box: IconPackage,
  Gift: IconGift,
  AudioLines: IconWaveSine,
  FileChartColumn: IconReportAnalytics,
  Landmark: IconBuildingBank,
  TabletSmartphone: IconDevices,
  SlidersHorizontal: IconAdjustmentsHorizontal,
  HandCoins: IconCoins,
  // Navigation: utilities + account menu
  Bell: IconBell,
  MessageCircle: IconMessageCircle,
  CircleHelp: IconHelpCircle,
  MapPin: IconMapPin,
  Rocket: IconRocket,
  Settings: IconSettings,
  LogOut: IconLogout,
  // Alerts row menu
  EllipsisVertical: IconDotsVertical,
  Mail: IconMail,
  MailOpen: IconMailOpened,
} satisfies Record<string, Icon>;

export type DesignIconName = keyof typeof DESIGN_ICONS;

/**
 * Time block type → icon, for the repo's block types (Break / Vacation /
 * Sick / Personal / Holiday / Other; decided 2026-10-08, types not renamed).
 * Unknown types use Other's.
 */
export const TIME_BLOCK_ICONS: Record<string, Icon> = {
  Break: IconToolsKitchen2,
  Vacation: IconPlane,
  Sick: IconFirstAidKit,
  Personal: IconUser,
  Holiday: IconBeach,
  Other: IconCalendarOff,
};

export const timeBlockIcon = (type: string | undefined): Icon =>
  (type && TIME_BLOCK_ICONS[type]) || IconCalendarOff;
